import { Close } from '@mui/icons-material';
import {
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';

import { humanizeEventLabel } from 'helpers/fileHistory';
import useTranslate from 'hooks/useTranslate';
import appThemeV2 from 'theme/themeV2';
import type { ActivityEventItem } from 'types';
import { formatDateTime } from 'utils/dateUtils';

interface EventDetailDialogProps {
    event: ActivityEventItem | null;
    onClose: () => void;
}

const DetailRow = ({ label, value }: { label: string; value?: string | null }) => (
    <Stack spacing={0.25}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {label}
        </Typography>
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {value || '—'}
        </Typography>
    </Stack>
);

const EventDetailDialogBody = ({ event, onClose }: EventDetailDialogProps) => {
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    if (!event) return null;

    const metaEntries = event.meta
        ? Object.entries(event.meta).filter(([, value]) => value != null && value !== '')
        : [];

    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile} scroll="paper">
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    pr: 1,
                }}
            >
                <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                    {translate('eventDetails')}
                    <Typography variant="body2" color="text.secondary">
                        {event.display_message || humanizeEventLabel(event.event_type)}
                    </Typography>
                </Stack>
                <IconButton onClick={onClose} size="small" aria-label={translate('close')}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ bgcolor: 'rgba(248, 250, 252, 0.55)', px: { xs: 2, sm: 3 } }}
            >
                <Stack spacing={2}>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        <Chip size="small" label={humanizeEventLabel(event.event_type)} />
                        <Chip size="small" variant="outlined" label={event.category} />
                        <Chip size="small" variant="outlined" label={event.severity} />
                    </Stack>
                    <DetailRow
                        label={translate('eventTimestamp')}
                        value={formatDateTime(event.timestamp)}
                    />
                    <DetailRow label={translate('eventUser')} value={event.user?.name} />
                    <DetailRow label={translate('email')} value={event.user?.email} />
                    <DetailRow label={translate('eventMessage')} value={event.display_message} />
                    {event.actor?.email && event.actor.email !== event.user?.email ? (
                        <DetailRow
                            label={translate('actor')}
                            value={
                                event.actor.name
                                    ? `${event.actor.name} (${event.actor.email})`
                                    : event.actor.email
                            }
                        />
                    ) : null}
                    {metaEntries.length ? (
                        <Stack spacing={1}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ fontWeight: 600 }}
                            >
                                {translate('eventMeta')}
                            </Typography>
                            {metaEntries.map(([key, value]) => (
                                <Typography
                                    key={key}
                                    variant="body2"
                                    sx={{ wordBreak: 'break-word' }}
                                >
                                    {humanizeEventLabel(key)}: {String(value)}
                                </Typography>
                            ))}
                        </Stack>
                    ) : null}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export const EventDetailDialog = ({ event, onClose }: EventDetailDialogProps) => {
    if (!event) return null;
    return (
        <ThemeProvider theme={appThemeV2}>
            <EventDetailDialogBody event={event} onClose={onClose} />
        </ThemeProvider>
    );
};
